import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, Copy, Check, User, Building2, Mail, Phone,
  Globe, Linkedin, Twitter, Instagram, Image, Palette,
  Type, Download, Eye, ChevronDown, ChevronUp,
} from "lucide-react";

// ── Signature Styles ──
const STYLES = [
  { id: "classic",   label: "Classic",   desc: "Clean and professional" },
  { id: "modern",    label: "Modern",    desc: "Bold with color accents" },
  { id: "minimal",   label: "Minimal",   desc: "Just the essentials" },
  { id: "corporate", label: "Corporate", desc: "Formal with logo space" },
];

const COLOR_PRESETS = [
  { label: "Coral",    primary: "#E85D4A", text: "#1E2A3A" },
  { label: "Navy",     primary: "#1E3A5F", text: "#1E2A3A" },
  { label: "Teal",     primary: "#0A8F7F", text: "#1E2A3A" },
  { label: "Purple",   primary: "#6C5CE7", text: "#1E2A3A" },
  { label: "Black",    primary: "#1E2A3A", text: "#1E2A3A" },
  { label: "Forest",   primary: "#2D6A4F", text: "#1E2A3A" },
  { label: "Crimson",  primary: "#C0392B", text: "#1E2A3A" },
  { label: "Slate",    primary: "#5A6872", text: "#1E2A3A" },
];

interface SigData {
  fullName: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  linkedin: string;
  twitter: string;
  instagram: string;
  photoUrl: string;
}

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function buildSignatureHtml(data: SigData, style: string, color: string): string {
  const n = escHtml(data.fullName);
  const t = escHtml(data.title);
  const c = escHtml(data.company);
  const e = escHtml(data.email);
  const p = escHtml(data.phone);
  const w = escHtml(data.website);
  const wClean = w.replace(/^https?:\/\//, "");

  // Social icons as text links (email-safe, no external image deps)
  const socials: string[] = [];
  if (data.linkedin) socials.push(`<a href="${escHtml(data.linkedin)}" style="color:${color};text-decoration:none;font-size:12px;font-weight:600;">LinkedIn</a>`);
  if (data.twitter) socials.push(`<a href="${escHtml(data.twitter)}" style="color:${color};text-decoration:none;font-size:12px;font-weight:600;">Twitter</a>`);
  if (data.instagram) socials.push(`<a href="${escHtml(data.instagram)}" style="color:${color};text-decoration:none;font-size:12px;font-weight:600;">Instagram</a>`);
  const socialsHtml = socials.length > 0
    ? `<p style="margin:8px 0 0 0;font-size:12px;">${socials.join(' &nbsp;·&nbsp; ')}</p>`
    : "";

  const photoHtml = data.photoUrl
    ? `<td style="vertical-align:top;padding-right:${style === "minimal" ? "12" : "16"}px;">
        <img src="${escHtml(data.photoUrl)}" alt="${n}" width="${style === "minimal" ? "50" : "70"}" height="${style === "minimal" ? "50" : "70"}" style="border-radius:${style === "modern" ? "12px" : "50%"};display:block;object-fit:cover;" />
      </td>`
    : "";

  const moremail = `<p style="margin:10px 0 0 0;font-size:10px;color:#9CA3AF;">Sent with <a href="${SITE_URL}?ref=sig" style="color:${color};text-decoration:none;">MoreMail</a></p>`;

  if (style === "minimal") {
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;">
  <tr>
    ${photoHtml}
    <td style="vertical-align:top;">
      <p style="margin:0;font-size:14px;font-weight:700;color:#1E2A3A;">${n}</p>
      ${t || c ? `<p style="margin:2px 0 0 0;font-size:12px;color:#5A6872;">${[t, c].filter(Boolean).join(" · ")}</p>` : ""}
      <p style="margin:6px 0 0 0;font-size:12px;color:#5A6872;">
        ${[e && `<a href="mailto:${e}" style="color:${color};text-decoration:none;">${e}</a>`, p, wClean && `<a href="${w || `https://${wClean}`}" style="color:${color};text-decoration:none;">${wClean}</a>`].filter(Boolean).join(" &nbsp;·&nbsp; ")}
      </p>
      ${socialsHtml}
      ${moremail}
    </td>
  </tr>
</table>`;
  }

  if (style === "modern") {
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;">
  <tr>
    ${photoHtml}
    <td style="vertical-align:top;border-left:3px solid ${color};padding-left:16px;">
      <p style="margin:0;font-size:16px;font-weight:800;color:#1E2A3A;">${n}</p>
      ${t ? `<p style="margin:2px 0 0 0;font-size:13px;font-weight:600;color:${color};">${t}</p>` : ""}
      ${c ? `<p style="margin:2px 0 0 0;font-size:12px;color:#5A6872;">${c}</p>` : ""}
      <p style="margin:10px 0 0 0;font-size:12px;color:#5A6872;line-height:1.8;">
        ${e ? `<a href="mailto:${e}" style="color:#5A6872;text-decoration:none;">✉ ${e}</a><br/>` : ""}
        ${p ? `☎ ${p}<br/>` : ""}
        ${wClean ? `<a href="${w || `https://${wClean}`}" style="color:${color};text-decoration:none;">🌐 ${wClean}</a>` : ""}
      </p>
      ${socialsHtml}
      ${moremail}
    </td>
  </tr>
</table>`;
  }

  if (style === "corporate") {
    return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;border-top:3px solid ${color};padding-top:12px;">
  <tr>
    ${photoHtml}
    <td style="vertical-align:top;">
      <p style="margin:0;font-size:15px;font-weight:700;color:#1E2A3A;">${n}</p>
      ${t ? `<p style="margin:2px 0 0 0;font-size:12px;color:#5A6872;">${t}</p>` : ""}
      ${c ? `<p style="margin:1px 0 0 0;font-size:12px;font-weight:600;color:${color};">${c}</p>` : ""}
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;">
        ${e ? `<tr><td style="font-size:12px;color:#5A6872;padding:1px 8px 1px 0;">Email</td><td style="font-size:12px;"><a href="mailto:${e}" style="color:${color};text-decoration:none;">${e}</a></td></tr>` : ""}
        ${p ? `<tr><td style="font-size:12px;color:#5A6872;padding:1px 8px 1px 0;">Phone</td><td style="font-size:12px;color:#1E2A3A;">${p}</td></tr>` : ""}
        ${wClean ? `<tr><td style="font-size:12px;color:#5A6872;padding:1px 8px 1px 0;">Web</td><td style="font-size:12px;"><a href="${w || `https://${wClean}`}" style="color:${color};text-decoration:none;">${wClean}</a></td></tr>` : ""}
      </table>
      ${socialsHtml}
      ${moremail}
    </td>
  </tr>
</table>`;
  }

  // Classic (default)
  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;">
  <tr>
    ${photoHtml}
    <td style="vertical-align:top;">
      <p style="margin:0;font-size:15px;font-weight:700;color:#1E2A3A;">${n}</p>
      ${t ? `<p style="margin:2px 0 0 0;font-size:13px;color:#5A6872;">${t}</p>` : ""}
      ${c ? `<p style="margin:1px 0 0 0;font-size:13px;color:${color};font-weight:600;">${c}</p>` : ""}
      <hr style="border:none;border-top:1px solid #E5E7EB;margin:10px 0;" />
      <p style="margin:0;font-size:12px;color:#5A6872;line-height:1.8;">
        ${e ? `<a href="mailto:${e}" style="color:${color};text-decoration:none;">${e}</a><br/>` : ""}
        ${p ? `${p}<br/>` : ""}
        ${wClean ? `<a href="${w || `https://${wClean}`}" style="color:${color};text-decoration:none;">${wClean}</a>` : ""}
      </p>
      ${socialsHtml}
      ${moremail}
    </td>
  </tr>
</table>`;
}

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Email Signature Generator",
  applicationCategory: "BusinessApplication",
  description: "Free email signature generator. Create a professional HTML signature for Gmail, Outlook, and Apple Mail in seconds.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function SignatureGeneratorPage() {
  const [data, setData] = useState<SigData>({
    fullName: "", title: "", company: "", email: "", phone: "",
    website: "", linkedin: "", twitter: "", instagram: "", photoUrl: "",
  });
  const [style, setStyle] = useState("classic");
  const [color, setColor] = useState("#E85D4A");
  const [copied, setCopied] = useState(false);
  const [showSocials, setShowSocials] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const update = useCallback((field: keyof SigData, value: string) => {
    setData(prev => ({ ...prev, [field]: value }));
  }, []);

  const hasContent = data.fullName.trim().length > 0;

  const signatureHtml = hasContent ? buildSignatureHtml(data, style, color) : "";

  async function handleCopy() {
    if (!previewRef.current || !hasContent) return;
    try {
      // Copy as rich HTML (so it pastes with formatting in email clients)
      const blob = new Blob([signatureHtml], { type: "text/html" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blob, "text/plain": new Blob([signatureHtml], { type: "text/plain" }) }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: copy as plain HTML text
      try {
        await navigator.clipboard.writeText(signatureHtml);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        // Last resort
        const ta = document.createElement("textarea");
        ta.value = signatureHtml;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    }
  }

  function InputField({ label, field, placeholder, icon: Icon, type }: {
    label: string; field: keyof SigData; placeholder: string; icon: any; type?: string;
  }) {
    return (
      <div>
        <label className="block text-xs font-semibold text-ink mb-1">{label}</label>
        <div className="relative">
          <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate/40" />
          <input
            type={type || "text"}
            value={data[field]}
            onChange={(e) => update(field, e.target.value)}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <SeoHead
        title="Free Email Signature Generator — Professional HTML Signatures for Gmail & Outlook | MoreMail"
        description="Create a professional email signature in seconds. Free generator for Gmail, Outlook, and Apple Mail. 4 styles, custom colors, social links, and photo support."
        canonical={`${SITE_URL}/email-signature-generator`}
        keywords="email signature generator, free email signature, HTML email signature, Gmail signature, Outlook signature, professional email signature, email signature template, email signature maker, email signature creator"
        ogTitle="Free Email Signature Generator — Create Professional Signatures in Seconds | MoreMail"
        ogDescription="Enter your details. Pick a style. Copy your signature. Works with Gmail, Outlook, Apple Mail, and more."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <Type size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            Email Signature <span className="text-coral">Generator</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Create a professional HTML email signature in seconds. Works with Gmail, Outlook, Apple Mail, and more.
          </p>
        </div>
      </section>

      {/* Generator */}
      <section className="py-10 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-6">

            {/* ── Inputs ── */}
            <div className="space-y-4">
              {/* Basic info */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <User size={15} className="text-coral" /> Your Details
                </h2>
                <div className="space-y-3">
                  <InputField label="Full Name *" field="fullName" placeholder="Jane Smith" icon={User} />
                  <InputField label="Job Title" field="title" placeholder="Marketing Director" icon={Building2} />
                  <InputField label="Company" field="company" placeholder="Acme Inc." icon={Building2} />
                  <InputField label="Email" field="email" placeholder="jane@acme.com" icon={Mail} type="email" />
                  <InputField label="Phone" field="phone" placeholder="+1 (555) 123-4567" icon={Phone} />
                  <InputField label="Website" field="website" placeholder="https://acme.com" icon={Globe} type="url" />
                  <InputField label="Photo URL" field="photoUrl" placeholder="https://..." icon={Image} type="url" />
                </div>
              </div>

              {/* Socials */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <button onClick={() => setShowSocials(!showSocials)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-ink">
                  <span className="flex items-center gap-2"><Linkedin size={14} className="text-coral" /> Social Links</span>
                  {showSocials ? <ChevronUp size={14} className="text-slate" /> : <ChevronDown size={14} className="text-slate" />}
                </button>
                {showSocials && (
                  <div className="mt-4 space-y-3">
                    <InputField label="LinkedIn" field="linkedin" placeholder="https://linkedin.com/in/janesmith" icon={Linkedin} type="url" />
                    <InputField label="Twitter / X" field="twitter" placeholder="https://twitter.com/janesmith" icon={Twitter} type="url" />
                    <InputField label="Instagram" field="instagram" placeholder="https://instagram.com/janesmith" icon={Instagram} type="url" />
                  </div>
                )}
              </div>

              {/* Style + color */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <Palette size={15} className="text-coral" /> Style & Color
                </h2>

                <label className="block text-xs font-semibold text-ink mb-2">Style</label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {STYLES.map(s => (
                    <button key={s.id} onClick={() => setStyle(s.id)}
                      className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${style === s.id ? "bg-coral/10 border-coral/30 text-coral font-semibold" : "border-sand text-slate hover:border-coral/20"}`}>
                      <span className="font-semibold block">{s.label}</span>
                      <span className="text-[10px] opacity-70">{s.desc}</span>
                    </button>
                  ))}
                </div>

                <label className="block text-xs font-semibold text-ink mb-2">Accent Color</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map(c => (
                    <button key={c.label} onClick={() => setColor(c.primary)}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${color === c.primary ? "border-ink scale-110 shadow-md" : "border-transparent hover:scale-105"}`}
                      style={{ backgroundColor: c.primary }}
                      title={c.label}
                    />
                  ))}
                  <div className="relative">
                    <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                      className="w-7 h-7 rounded-full cursor-pointer border-2 border-sand" title="Custom color" />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Preview + Copy ── */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-sand p-5 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display font-bold text-ink text-sm flex items-center gap-2">
                    <Eye size={15} className="text-coral" /> Live Preview
                  </h2>
                  {hasContent && (
                    <button onClick={handleCopy}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        copied ? "bg-teal-light text-teal border border-teal/20" : "bg-coral text-white hover:bg-coral-dark"
                      }`}>
                      {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy Signature</>}
                    </button>
                  )}
                </div>

                {/* Simulated email client */}
                <div className="bg-paper rounded-lg border border-sand/80 overflow-hidden">
                  {/* Email header simulation */}
                  <div className="px-4 py-3 border-b border-sand/80 bg-white">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-slate/20" />
                      <span className="text-[10px] text-slate">To: recipient@company.com</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-slate/20" />
                      <span className="text-[10px] text-slate">Subject: Quick follow-up</span>
                    </div>
                  </div>

                  {/* Email body */}
                  <div className="px-4 py-4">
                    <p className="text-xs text-slate/40 mb-4 italic">Hi there, thanks for the chat today...</p>
                    <p className="text-xs text-slate/40 mb-6 italic">Best regards,</p>

                    {/* Signature */}
                    {hasContent ? (
                      <div ref={previewRef} dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                    ) : (
                      <div className="text-center py-8">
                        <User size={32} className="mx-auto text-slate/20 mb-2" />
                        <p className="text-xs text-slate/40">Enter your name to see a preview</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Copy instructions */}
                {hasContent && (
                  <div className="mt-4 bg-paper rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-ink mb-2">How to use your signature:</h3>
                    <ol className="text-[11px] text-slate space-y-1.5 list-decimal list-inside">
                      <li>Click <strong>"Copy Signature"</strong> above</li>
                      <li><strong>Gmail:</strong> Settings → See all settings → Signature → Paste</li>
                      <li><strong>Outlook:</strong> Settings → Mail → Compose → Email signature → Paste</li>
                      <li><strong>Apple Mail:</strong> Preferences → Signatures → Paste</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Why Use a Professional <span className="text-coral">Email Signature</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "First Impressions Matter", desc: "Every email you send is a chance to look professional. A polished signature builds credibility instantly.", icon: User },
              { title: "Free Marketing", desc: "Your signature is seen hundreds of times per week. Include your website, socials, and brand — it's free advertising.", icon: Globe },
              { title: "Works Everywhere", desc: "Our signatures use email-safe HTML that renders perfectly in Gmail, Outlook, Apple Mail, and every major client.", icon: Mail },
            ].map(item => (
              <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
                <item.icon size={22} className="mx-auto text-coral mb-3" />
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Need More Than a Signature?
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            MoreMail gives you AI-powered email campaigns, pre-built automations,
            and deliverability tools to grow your business with email.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
